<template>
  <div class="container">
    <div class="account-left">
      <div class="account-balance">
        <div class="account-balance-bg">
          <div class="content">
            <div class="total-assets">{{ $t('AccountInfo.AccountBalance.totalAssets') }}</div>
            <div class="balance-container">
              <div class="balance">￥<span class="amount">{{ accountInfo.balance }}</span></div>
              <div class="freeze-amount">
                {{ $t('AccountInfo.AccountBalance.frozenAmount') }}：￥<span>{{ accountInfo.freeze }}</span>
              </div>
            </div>
            <div class="balance-actions">
              <div class="action"
                   @click="toggleRechargeModal">
                <span class="action-recharge-icon"></span>{{ $t('AccountInfo.AccountBalance.recharge') }}
              </div>
              <div class="divider">|</div>
              <div class="action"
                   @click="toggleWithdrawModal">
                <span class="action-withdraw-icon"></span>{{ $t('AccountInfo.AccountBalance.withdraw') }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DesignBill />
    </div>
    <div class="account-right">
      <div class="menu-container">
        <a-menu mode="horizontal"
                :selected-keys="menuSelectedKey"
                @click="handleMenuClick">
          <a-menu-item key="recharge"
                       class="menuStyle">{{ $t('AccountInfo.AccountBalance.rechargeRecords') }}</a-menu-item>
          <a-menu-item key="withdraw"
                       class="menuStyle">{{ $t('AccountInfo.AccountBalance.withdrawRecords') }}</a-menu-item>
          <a-menu-item key="usage"
                       class="menuStyle">{{ $t('AccountInfo.AccountBalance.usageRecords') }}</a-menu-item>
        </a-menu>
      </div>
      <div class="table-container">
        <a-scroll-table class="scrollTable"
                        :data-source="dataSource"
                        :pagination="pagination"
                        :columns="columns"
                        @change="pageChange">
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'route'">
              <span>{{ formatRoute(record.route) }}</span>
            </template>
            <template v-else-if="column.dataIndex === 'start_at'">
              <span>{{ formatStartAt(record.start_at) }}</span>
            </template>
            <template v-else-if="column.dataIndex === 'createdAt'">
              <span>{{ formatStartAt(record.start_at) }}</span>
            </template>
            <template v-else-if="column.dataIndex === 'status'">
              <span :style="{ color: formatStatus(record.status, menuSelectedKey[0] === 'recharge').color }">{{
                formatStatus(record.status, menuSelectedKey[0] === 'recharge').text }}</span>
            </template>
          </template>
        </a-scroll-table>
      </div>
    </div>
  </div>
  <rechargeModal :visible="rechargeVisible"
                 @submit="handleRechargeSubmit"
                 @close="toggleRechargeModal" />
  <withdrawModal :visible="withdrawVisible"
                 :account="accountInfo"
                 @submit="handleWithdrawSubmit"
                 @close="toggleWithdrawModal" />
  <rechargeWechatModal :visible="wechatPayVisible"
                       :url="rechargeQRCodeUrl"
                       :order-number="rechargeOrderNumber"
                       @close="handlePayClose" />
  <rechargeAliPayModal :visible="aliPayVisible"
                       :url="rechargeQRCodeUrl"
                       :order-number="rechargeOrderNumber"
                       @close="handlePayClose" />
</template>

<script setup lang="ts">
import { AScrollTable } from '@iota-cn/ui-components'
import { message } from 'ant-design-vue'
import dayjs from 'dayjs'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAccountBalance, getRechargeBill, getUsageBill, getWithdrawBill, rechargeFromAliPay, rechargeFromWechat, withdraw } from '../api/balance'
import type { AccountRechargeModel, AccountWithdrawModel, BillItemModel, PaginatedResult, WithdrawRequestParams } from '../api/interface/balanceInterface'
import rechargeModal from '../components/BalanceRecharge.vue'
import rechargeAliPayModal from '../components/BalanceRecharge_alipay.vue'
import rechargeWechatModal from '../components/BalanceRecharge_wechat.vue'
import withdrawModal from '../components/BalanceWithdraw.vue'
import DesignBill from "./DesignBill.vue"

const dataSource = ref<AccountRechargeModel[] | BillItemModel[]>([])
const { t } = useI18n()
const menuSelectedKey = ref<string[]>(['recharge'])
const rechargeVisible = ref(false)
const wechatPayVisible = ref(false)
const withdrawVisible = ref(false)
const aliPayVisible = ref(false)

const paginationLimit = ref<number>(10)
const paginationOffset = ref<number>(0)
const rechargeQRCodeUrl = ref<string>()
const rechargeOrderNumber = ref<number>()
const accountInfo = ref({
  balance: 0,
  freeze: 0
})
const pagination = ref({
  total: 0,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '40', '80'],
  showTotal: (total: number) => `${t('AccountInfo.AccountBalance.totalRecords')} ${total} ${t('AccountInfo.AccountBalance.records')}`,
  size: 'small',
})

const pageChange = async (item: any) => {
  paginationLimit.value = item.pageSize
  paginationOffset.value = (item.current - 1) * item.pageSize
  if (menuSelectedKey.value[0] === 'recharge') {
    await getRechargeBill(paginationLimit.value, paginationOffset.value).then(handleBillResult)
  } else if (menuSelectedKey.value[0] === 'withdraw') {
    await getWithdrawBill(paginationLimit.value, paginationOffset.value).then(handleBillResult)
  } else if (menuSelectedKey.value[0] === 'usage') {
    await getUsageBill(paginationLimit.value, paginationOffset.value).then(handleBillResult)
  }
  pagination.value.pageSize = item.pageSize
}

const rechargeColumns = computed(() => [
  {
    title: t('AccountInfo.AccountBalance.rechargeMethod'),
    dataIndex: 'route',
    key: 'route',
    width: '20%',
    align: 'center',
  },
  {
    title: t('AccountInfo.AccountBalance.rechargeTime'),
    dataIndex: 'start_at',
    key: 'start_at',
    width: '30%',
    align: 'center',
  },
  {
    title: t('AccountInfo.AccountBalance.rechargeAmount'),
    dataIndex: 'amount',
    key: 'amount',
    width: '30%',
    align: 'center',
    customCell: () => {
      return {
        style: {
          color: "#DE4A09"
        }
      }
    },
  },
  {
    title: t('AccountInfo.AccountBalance.rechargeStatus'),
    dataIndex: 'status',
    key: 'status',
    width: '20%',
    align: 'center',
  },
])

const withdrawColumns = computed(() => [
  {
    title: t('AccountInfo.AccountBalance.withdrawMethod'),
    dataIndex: 'route',
    key: 'route',
    width: '20%',
    align: 'center',
  },
  {
    title: t('AccountInfo.AccountBalance.withdrawTime'),
    dataIndex: 'start_at',
    key: 'start_at',
    width: '30%',
    align: 'center'
  },
  {
    title: t('AccountInfo.AccountBalance.withdrawAmount'),
    dataIndex: 'amount',
    key: 'amount',
    width: '30%',
    align: 'center',
    customCell: () => {
      return {
        style: {
          color: "#DE4A09"
        }
      }
    },
  },
  {
    title: t('AccountInfo.AccountBalance.withdrawStatus'),
    dataIndex: 'status',
    key: 'status',
    width: '20%',
    align: 'center',
  },
])

const usageColumns = computed(() => [
  {
    title: t('AccountInfo.AccountBalance.orderNumber'),
    dataIndex: 'number',
    key: 'number',
    width: '40%',
    align: 'center',
  },
  {
    title: t('AccountInfo.AccountBalance.orderTime'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: '40%',
    align: 'center',
  },
  {
    title: t('AccountInfo.AccountBalance.expenditureAmount'),
    dataIndex: 'amount',
    key: 'amount',
    width: '20%',
    align: 'center',
    customCell: () => {
      return {
        style: {
          color: "#DE4A09"
        }
      }
    },
  }
])

const columns = computed(() => {
  if (menuSelectedKey.value[0] === 'recharge') {
    return rechargeColumns
  } else if (menuSelectedKey.value[0] === 'withdraw') {
    return withdrawColumns
  } else {
    return usageColumns
  }
})

const handleBillResult = (billResult: PaginatedResult<AccountRechargeModel> | PaginatedResult<AccountWithdrawModel> | PaginatedResult<BillItemModel>) => {
  dataSource.value = billResult.rows
  pagination.value.total = billResult.count
}

const formatRoute = computed(() => (route: 0 | 1) =>
  route === 0 ? t('AccountInfo.AccountBalance.alipay') : t('AccountInfo.AccountBalance.wechat')
)

const formatStartAt = (startAt: string) => {
  return dayjs(startAt).format('YYYY-MM-DD HH:mm:ss')
}

const formatStatus = computed(() => (status: 2 | 1 | 0, isRecharge: boolean) => {
  const rechargeStatusMap = {
    2: { text: t('AccountInfo.AccountBalance.waitingForRecharge'), color: 'black' },
    1: { text: t('AccountInfo.AccountBalance.rechargeSuccess'), color: 'green' },
    0: { text: t('AccountInfo.AccountBalance.rechargeFailed'), color: 'red' }
  }
  const withdrawStatusMap = {
    2: { text: t('AccountInfo.AccountBalance.waitingForWithdraw'), color: 'black' },
    1: { text: t('AccountInfo.AccountBalance.withdrawSuccess'), color: 'green' },
    0: { text: t('AccountInfo.AccountBalance.withdrawFailed'), color: 'red' }
  }
  return isRecharge ? rechargeStatusMap[status] : withdrawStatusMap[status]
})

const toggleRechargeModal = () => {
  rechargeVisible.value = !rechargeVisible.value
}

const handlePayClose = async (route?: 0 | 1 | undefined) => {
  // 切换支付可见性
  if (route !== undefined) {
    aliPayVisible.value = route === 0 ? !aliPayVisible.value : false
    wechatPayVisible.value = route === 1 ? !wechatPayVisible.value : false
  } else {
    aliPayVisible.value = false
    wechatPayVisible.value = false
  }

  const BillCall = menuSelectedKey.value[0] === 'recharge' ? getRechargeBill : (menuSelectedKey.value[0] === 'withdraw' ? getWithdrawBill : getUsageBill)
  await BillCall(paginationLimit.value, paginationOffset.value).then(handleBillResult)

  await checkAccountBalance()
}

const handleRechargeSubmit = async (data: { amount: number, route: 0 | 1 }) => {
  /**
   * route:0 支付宝充值
   * route:1 微信充值
   */
  if (data.route === 1) {
    await rechargeFromWechat(data.amount).then(wechatPayOrder => {
      rechargeQRCodeUrl.value = wechatPayOrder.url
      rechargeOrderNumber.value = wechatPayOrder.orderNumber
    })
  } else {
    await rechargeFromAliPay(data.amount).then(aliPayOrder => {
      rechargeQRCodeUrl.value = aliPayOrder.url
      rechargeOrderNumber.value = aliPayOrder.orderNumber
    })
  }
  toggleRechargeModal()
  handlePayClose(data.route)
}

const handleWithdrawSubmit = async (withdrawForm: WithdrawRequestParams) => {
  try {
    await withdraw(withdrawForm).then(async withdrawResult => {
      if (menuSelectedKey.value[0] === 'withdraw') {
        await getWithdrawBill(paginationLimit.value, paginationOffset.value).then(handleBillResult)
      }
      message.success(t('AccountInfo.AccountBalance.withdrawSuccessMessage'))
      checkAccountBalance()
      toggleWithdrawModal()
    })
  } catch (error: any) {
    message.error(error.detail)
  }
}

const toggleWithdrawModal = () => {
  withdrawVisible.value = !withdrawVisible.value
}

const checkAccountBalance = async () => {
  await getAccountBalance().then(result => {
    accountInfo.value = result
  })
}

checkAccountBalance()

// 监听 menuSelectedKey 的变化并调用相应的方法
watch(menuSelectedKey, async (newKey) => {
  dataSource.value = [] // 初始化 dataSource

  if (newKey[0] === 'recharge') {
    await getRechargeBill(10, 0).then(handleBillResult)
  } else if (newKey[0] === 'withdraw') {
    await getWithdrawBill(10, 0).then(handleBillResult)
  } else if (newKey[0] === 'usage') {
    await getUsageBill(10, 0).then(handleBillResult)
  }
}, { immediate: true })

const handleMenuClick = (menuProps: { key: string }) => {
  menuSelectedKey.value = [menuProps.key]
}
</script>


<style scoped lang="less">
.container {
  display: flex;
  height: 100%;
  width: 100%;
  padding: 5px;
  box-sizing: border-box;
}

.account-left {
  flex: 1;
  margin: 20px;
  box-shadow: 0px 0px 14px 0px rgba(185, 175, 175, 0.1),
    2px 2px 10px rgba(0, 0, 0, 0.1);
}

.account-balance {
  flex: 1;
  margin: 20px;
}

.account-balance-bg {
  width: 100%;
  height: 200px;
  background: linear-gradient(45deg, #505050, #111111);
  border-radius: 16px;
  background: url("./assets/imgs/balanceBg.png") no-repeat;
  background-size: 100% 100%;
}


.content {
  padding: 24px 24px 24px 24px;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.total-assets {
  color: #fff;
  font-size: 16px;
  font-family: Microsoft YaHei;
}


.balance-container {
  display: flex;
  align-items: center;
  margin-top: 20px;

  .balance {
    color: #fff;
    font-size: 16px;
    font-family: Microsoft YaHei;

    .amount {
      font-family: Microsoft YaHei;
      font-size: 30px;
      margin-left: 5px;
    }
  }

  .freeze-amount {
    margin-top: 6px;
    margin-left: 10px;
    font-size: 14px;
    color: #fff;
    font-family: Microsoft YaHei;
    padding: 6px;
    background-color: rgba(255, 255, 255, 0.15);
    position: relative;
  }

  .freeze-amount::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -12px;
    margin-top: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent rgba(255, 255, 255, 0.15) transparent transparent;
  }
}

.balance-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.action {
  width: 100%;
  text-align: center;
  color: #fff;
  font-size: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}


.action-recharge-icon {
  margin-top: 2px;
  margin-right: 8px;
  background: url("./assets/imgs/balanceRecharge.png") no-repeat;
  background-size: 100%;
  display: inline-flex;
  width: 14px;
  height: 14px;
}

.action-withdraw-icon {
  margin-right: 8px;
  background: url("./assets/imgs/balanceWithdraw.png") no-repeat;
  background-size: 100%;
  display: inline-flex;
  width: 14px;
  height: 14px;
}

.divider {
  margin: 0 10px;
  color: #fff;
  opacity: 0.25;
  font-size: 16px;
}

.account-right {
  flex: 3;
  overflow: hidden;
}

:deep(.ant-table-thead) {
  .ant-table-cell {
    font-family: Microsoft YaHei;
    font-weight: bold;
    color: #111111;
  }
}

:deep(.ant-menu-item-selected) {
  background-color: #fff !important;

  .ant-menu-title-content {
    color: #111111 !important;
    font-weight: bold;
  }
}

:deep(.menuStyle) {
  font-family: Microsoft YaHei;
  font-size: 16px;

  .ant-menu-title-content {
    color: #999999;
  }

}

:deep(.ant-menu-item-active) {
  background-color: #fff !important;

  .ant-menu-title-content {
    color: #111111;
    font-family: Microsoft YaHei;
    font-weight: bold;
  }
}

:deep(.ant-table-body) {
  height: calc(100vh - 500px)
}

:deep(.ant-table-row) {
  height: 69px;
}

.menu-container {
  margin-bottom: 20px;
}

.table-container {
  height: 100%;
}

.scrollTable {
  height: 100%;
  width: 100%;
  overflow: hidden;
}
</style>
